import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, TextField, Button, Typography, Alert, Paper } from '@mui/material';
import { supabase } from '../lib/supabase';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    // Get email from URL query params
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !otp || !password || !confirmPassword) {
      setError(t('auth.fillAllFields'));
      return;
    }

    if (otp.length < 6) {
      setError(t('auth.invalidOtpLength'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.passwordsMismatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('auth.passwordTooShort'));
      return;
    }

    setLoading(true);

    try {
      // Verify OTP and update password
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'recovery'
      });

      if (verifyError) throw verifyError;

      // Now update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) throw updateError;

      setSuccess(true);
      
      // Sign out and redirect to login after a delay
      setTimeout(async () => {
        await supabase.auth.signOut();
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      setError(err.message || t('auth.resetPasswordError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%', mx: 2 }}>
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            component="img"
            src="/logo192.png"
            alt="PCU Logo"
            sx={{ width: 80, height: 80, objectFit: 'contain', display: 'block', mx: 'auto' }}
          />
        </Box>

        <Typography variant="h5" component="h1" gutterBottom align="center" sx={{ mb: 3 }}>
          {t('auth.resetPassword')}
        </Typography>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('auth.passwordResetSuccess')}
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!success && (
          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('auth.enterOtpAndNewPassword')}
            </Typography>

            <TextField
              fullWidth
              label={t('auth.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              margin="normal"
              disabled={!!searchParams.get('email')}
            />

            <TextField
              fullWidth
              label={t('auth.verificationCode')}
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
              margin="normal"
              inputProps={{ maxLength: 8 }}
              placeholder={t('auth.enterVerificationCode')}
              autoFocus
            />

            <TextField
              fullWidth
              label={t('auth.newPassword')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              margin="normal"
              autoComplete="new-password"
            />

            <TextField
              fullWidth
              label={t('auth.confirmPassword')}
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              margin="normal"
              autoComplete="new-password"
            />

            <Button fullWidth variant="contained" type="submit" disabled={loading} sx={{ mt: 3 }}>
              {loading ? t('common.loading') : t('auth.updatePassword')}
            </Button>

            <Button 
              fullWidth 
              variant="text" 
              onClick={() => navigate('/login')} 
              sx={{ mt: 2 }}
              disabled={loading}
            >
              {t('auth.backToLogin')}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ResetPassword;
